import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Modal, Form, Row, Col, Alert, Badge, Spinner, ButtonGroup } from 'react-bootstrap';
import { Trash2, Edit, Plus, Mail, Phone, MapPin, Link as LinkIcon, Building, Grid, List } from 'lucide-react';
import contactService from '../../../services/contactService';
import { Contact } from '../../../services/types';
import { useUserData } from '../../../hooks';
import ContactsListView from '../../../components/contacts/ContactsListView';
import { ERROR_MESSAGES, handleError } from '../../../utils/errorHandler';

type ViewMode = 'table' | 'list';

const Contacts = () => {
    const { canDelete } = useUserData();
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingContact, setEditingContact] = useState<Contact | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>('table');

    // Form state
    const [formData, setFormData] = useState<Partial<Contact>>({
        name: '',
        company_name: '',
        link: '',
        email: '',
        number: '',
        location: '',
        address: '',
        type: '',
        observation: '',
    });

    // Detect mobile and force list view
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth <= 920;
            setIsMobile(mobile);
            // Force list view on mobile/tablet
            if (mobile) {
                setViewMode('list');
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        loadContacts();
    }, []);

    const loadContacts = async () => {
        try {
            setLoading(true);
            const data = await contactService.getAll();
            setContacts(data);
        } catch (err) {
            setError(handleError(err, ERROR_MESSAGES.CONTACTS_LOAD_FAILED));
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (contact?: Contact) => {
        if (contact) {
            setEditingContact(contact);
            setFormData(contact);
        } else {
            setEditingContact(null);
            setFormData({
                name: '',
                company_name: '',
                link: '',
                email: '',
                number: '',
                location: '',
                address: '',
                type: '',
                observation: '',
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingContact(null);
        setError(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        try {
            if (editingContact) {
                await contactService.update(editingContact.public_id, formData);
                setSuccess('Contact updated successfully');
            } else {
                await contactService.create(formData);
                setSuccess('Contact created successfully');
            }
            handleCloseModal();
            loadContacts();
        } catch (err: any) {
            setError(handleError(err, editingContact ? ERROR_MESSAGES.CONTACTS_UPDATE_FAILED : ERROR_MESSAGES.CONTACTS_CREATE_FAILED));
        }
    };

    const handleDelete = async (contact: Contact) => {
        if (!window.confirm(`Are you sure you want to delete ${contact.name}?`)) {
            return;
        }

        try {
            await contactService.delete(contact.public_id);
            setSuccess('Contact deleted successfully');
            loadContacts();
        } catch (err) {
            setError(handleError(err, ERROR_MESSAGES.CONTACTS_DELETE_FAILED));
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value || null,
        }));
    };

    const getTypeColor = (type?: string | null) => {
        switch (type?.toLowerCase()) {
            case 'supplier': return 'primary';
            case 'client': return 'success';
            case 'partner': return 'info';
            case 'vendor': return 'warning';
            default: return 'secondary';
        }
    };

    const getWhatsAppLink = (phoneNumber: string) => {
        // Remove all non-numeric characters except +
        const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
        return `https://wa.me/${cleanNumber}`;
    };

    return (
        <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
            <div className={`d-flex ${isMobile ? 'flex-column gap-2' : 'justify-content-between align-items-center'} mb-4`}>
                {!isMobile && (
                    <div>
                        <h4 className="mb-0">Partners</h4>
                        <p className="text-muted small mb-0">Manage your business contacts</p>
                    </div>
                )}
                <div className={`d-flex gap-2 ${isMobile ? 'w-100' : ''}`}>
                    {/* View Toggle */}
                    <ButtonGroup className={isMobile ? 'flex-grow-1' : ''}>
                        <Button
                            variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                            onClick={() => setViewMode('table')}
                            title={isMobile ? "Table view not available on mobile" : "Table View"}
                            className={isMobile ? 'flex-grow-1' : ''}
                            disabled={isMobile}
                            style={{
                                opacity: isMobile ? 0.6 : 1,
                                cursor: isMobile ? 'not-allowed' : 'pointer',
                                backgroundColor: isMobile ? '#e9ecef' : undefined,
                                borderColor: isMobile ? '#dee2e6' : undefined,
                                color: isMobile ? '#6c757d' : undefined
                            }}
                        >
                            <Grid size={18} />
                        </Button>
                        <Button
                            variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
                            onClick={() => setViewMode('list')}
                            title="Card View"
                            className={isMobile ? 'flex-grow-1' : ''}
                        >
                            <List size={18} />
                        </Button>
                    </ButtonGroup>

                    {canDelete && (
                        <Button variant="primary" onClick={() => handleOpenModal()} className={isMobile ? 'w-100' : ''}>
                            <Plus size={18} className="me-2" />
                            {!isMobile && 'New Contact'}
                        </Button>
                    )}
                </div>
            </div>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Loading State */}
            {loading ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Loading contacts...</p>
                    </Card.Body>
                </Card>
            ) : contacts.length === 0 ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <Building size={48} className="mb-3 opacity-25" />
                        <p className="text-muted">No contacts yet</p>
                        {canDelete && (
                            <Button variant="primary" onClick={() => handleOpenModal()}>
                                <Plus size={18} className="me-2" />
                                Add First Contact
                            </Button>
                        )}
                    </Card.Body>
                </Card>
            ) : viewMode === 'list' ? (
                <ContactsListView
                    contacts={contacts}
                    canDelete={canDelete}
                    onEdit={handleOpenModal}
                    onDelete={handleDelete}
                />
            ) : (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-0">
                        <Table responsive hover className="mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th>Name</th>
                                    <th>Company</th>
                                    <th>Contact Info</th>
                                    <th>Location</th>
                                    <th>Type</th>
                                    {canDelete && <th style={{ width: '120px' }}>Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {contacts.map((contact) => (
                                    <tr key={contact.public_id}>
                                        <td>
                                            <div className="fw-bold">{contact.name}</div>
                                            {contact.observation && (
                                                <small className="text-muted">{contact.observation.substring(0, 50)}</small>
                                            )}
                                        </td>
                                        <td>
                                            {contact.company_name ? (
                                                <div>
                                                    <Building size={14} className="me-1 text-muted" />
                                                    {contact.company_name}
                                                </div>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="small">
                                                {contact.email && (
                                                    <div className="mb-1">
                                                        <Mail size={12} className="me-1 text-muted" />
                                                        <a href={`mailto:${contact.email}`}>{contact.email}</a>
                                                    </div>
                                                )}
                                                {contact.number && (
                                                    <div className="mb-1">
                                                        <Phone size={12} className="me-1 text-muted" />
                                                        <a href={getWhatsAppLink(contact.number)} target="_blank" rel="noopener noreferrer">{contact.number}</a>
                                                    </div>
                                                )}
                                                {contact.link && (
                                                    <div>
                                                        <LinkIcon size={12} className="me-1 text-muted" />
                                                        <a href={contact.link} target="_blank" rel="noopener noreferrer">
                                                            Website
                                                        </a>
                                                    </div>
                                                )}
                                                {!contact.email && !contact.number && !contact.link && (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            {contact.location ? (
                                                <div className="small">
                                                    <MapPin size={12} className="me-1 text-muted" />
                                                    {contact.location}
                                                </div>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td>
                                            {contact.type ? (
                                                <Badge bg={getTypeColor(contact.type)}>
                                                    {contact.type}
                                                </Badge>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        {canDelete && (
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => handleOpenModal(contact)}
                                                    >
                                                        <Edit size={14} />
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleDelete(contact)}
                                                    >
                                                        <Trash2 size={14} />
                                                    </Button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}

            {/* Create/Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingContact ? 'Edit Contact' : 'New Contact'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formData.name || ''}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Contact name"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Company Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="company_name"
                                        value={formData.company_name || ''}
                                        onChange={handleInputChange}
                                        placeholder="Company name"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={formData.email || ''}
                                        onChange={handleInputChange}
                                        placeholder="email@example.com"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Phone Number</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="number"
                                        value={formData.number || ''}
                                        onChange={handleInputChange}
                                        placeholder="+34 123 456 789"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Website/Link</Form.Label>
                                    <Form.Control
                                        type="url"
                                        name="link"
                                        value={formData.link || ''}
                                        onChange={handleInputChange}
                                        placeholder="https://example.com"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Type</Form.Label>
                                    <Form.Select
                                        name="type"
                                        value={formData.type || ''}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select type...</option>
                                        <option value="supplier">Supplier</option>
                                        <option value="client">Client</option>
                                        <option value="partner">Partner</option>
                                        <option value="vendor">Vendor</option>
                                        <option value="contractor">Contractor</option>
                                        <option value="service">Service Provider</option>
                                        <option value="other">Other</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Location</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="location"
                                        value={formData.location || ''}
                                        onChange={handleInputChange}
                                        placeholder="City, Country"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Address</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="address"
                                value={formData.address || ''}
                                onChange={handleInputChange}
                                placeholder="Full address"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Observation/Notes</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="observation"
                                value={formData.observation || ''}
                                onChange={handleInputChange}
                                placeholder="Additional notes about this contact"
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            {editingContact ? 'Update Contact' : 'Create Contact'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default Contacts;
